import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { NotificationService } from '../../../../sharedFeatures/services/notification.service';
import { UserLoggedIn } from '../../../../sharedFeatures/models/user-login.model';
import { PageTitleService } from '../../../../sharedFeatures/services/page-title.service';
import { Login } from '../../models/login';
import { LoginService } from '../../services/login.service';
import { LanguageService } from 'src/app/sharedFeatures/services/language';
import { AuthGuard } from 'src/app/sharedFeatures/services/auth-guard.service';
import { CurrentUserService } from 'src/app/sharedFeatures/services/current-user.service';
import { ModuleSelectionService } from '../../services/module-selection.service';
import { UmRole } from 'src/app/modules/roles/models/role-enum';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  language!: string;
  loginForm!: FormGroup;
  subscriptionTranslate!: Subscription;
  currentUser: UserLoggedIn | null = null;
  subscriptions: Subscription[] = [];
  hidePassword: boolean = true;
  isLoading: boolean = false;
  readonly usernameErrors = { required: 'users.login.validation.usernameRequired' };
  readonly passwordErrors = { required: 'users.login.validation.passwordRequired' };
  ChangeDetictionForceUpdateVar: boolean[] = [this.hidePassword];
  oldLoginScreenUrl: string = environment.LoginScreen.replace(
    '/Account/LogOff',
    ''
  );
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    public languageService: LanguageService,
    private notificationService: NotificationService,
    private loginService: LoginService,
    private pageTitle: PageTitleService,
    private changeDetectorRef: ChangeDetectorRef,
    private authGuard: AuthGuard,
    private currentUserService: CurrentUserService,
    private moduleSelectionService: ModuleSelectionService
  ) {}
  changeLang(lang: string) {
    this.translateService.use(lang);
    localStorage.setItem('currentLang', lang);
    this.languageService.setLanguage(lang);
  }
  ngOnInit(): void {
    this.setPageTitle();
    this.buildForm();
    this.setLanguageSubscriber();
  }
  hidePasswordIcon() {
    this.hidePassword = !this.hidePassword;
    this.ChangeDetictionForceUpdateVar = [this.hidePassword];
    this.changeDetectorRef.detectChanges();
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  submit(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;
      let loginModel: Login = {
        userName: this.loginForm.controls['userName'].value,
        password: this.loginForm.controls['password'].value,
      };
      this.subscriptions.push(
        this.loginService
          .login(loginModel)
          .pipe(
            switchMap((res: UserLoggedIn) => {
              // Store user data
              this.currentUser = res;
              this.currentUserService.setCurrentUser(res);

              // Store login credentials temporarily in sessionStorage for re-authentication
              // This is needed when user selects a module/role to get updated token
              // SessionStorage is cleared when browser/tab closes for security
              sessionStorage.setItem(
                'tempLoginCredentials',
                JSON.stringify({
                  userName: loginModel.userName,
                  password: loginModel.password,
                })
              );

              // Fetch modules to check if we can skip module selection screen
              return this.moduleSelectionService.getUserModules();
            })
          )
          .subscribe(
            (modules: any[]) => {
              // Get return URL from query params or session
              const returnUrl =
                this.route.snapshot.queryParams['returnUrl'] ||
                this.currentUserService.getReturnUrl();

              // Check if user has only one module with one role
              if (
                modules.length === 1 &&
                modules[0].roles &&
                modules[0].roles.length === 1
              ) {
                // Single module with single role - skip module selection screen
                const module = modules[0];
                const roleId = module.roles[0].id;

                // Re-authenticate with module and role to get updated token
                const credentials = JSON.parse(
                  sessionStorage.getItem('tempLoginCredentials') || '{}'
                );

                this.loginService
                  .loginWithModuleAndRole(
                    credentials.userName,
                    credentials.password,
                    module.id,
                    roleId
                  )
                  .subscribe(
                    (userDetails: UserLoggedIn) => {
                      // Update current user with token that includes role information
                      this.currentUserService.setCurrentUser(userDetails);

                      // Clear stored credentials
                      sessionStorage.removeItem('tempLoginCredentials');

                      // Get redirect URL based on module and role
                      const redirectUrl =
                        this.moduleSelectionService.getModuleRedirectUrl(
                          module.id,
                          roleId
                        );

                      // Navigate to appropriate page
                      if (returnUrl) {
                        // Check if returnUrl belongs to the module
                        if (this.shouldUseReturnUrl(module.id, returnUrl)) {
                          this.router.navigateByUrl(returnUrl);
                        } else {
                          this.router.navigateByUrl(redirectUrl);
                        }
                      } else {
                        this.router.navigateByUrl(redirectUrl);
                      }
                    },
                    (error: any) => {
                      // this.notificationService.showErrorTranslated(
                      //   'error.shared.operationFailed',
                      //   ''
                      // );
                    }
                  );
              } else {
                // Multiple modules or multiple roles - show module selection screen
                if (returnUrl) {
                  this.router.navigate(['/modules'], {
                    queryParams: { returnUrl: returnUrl },
                  });
                } else {
                  this.router.navigate(['/modules']);
                }
              }
            },
            (error: any) => {
              this.isLoading = false;
              if (error.status == 400) {
                let key = 'error.400';
                this.translateService.get([key]).subscribe(res => {
                  this.notificationService.showErrorTranslated(
                    `${res[key]}`,
                    ''
                  );
                });
              } else if (error.status == 500) {
                this.notificationService.showErrorTranslated(
                  'users.login.invalidCredentials',
                  ''
                );
              } else {
                this.notificationService.showErrorTranslated(
                  'users.login.invalidCredentials',
                  ''
                );
              }
            },
            () => {}
          )
      );
    } else {
      const loginFormFormKeys = Object.keys(this.loginForm.controls);
      loginFormFormKeys.forEach(control => {
        this.loginForm.controls[control].markAsTouched();
      });
    }
  }

  /**
   * Check if the returnUrl belongs to the selected module
   */
  private shouldUseReturnUrl(
    moduleId: number,
    returnUrl: string | null
  ): boolean {
    if (!returnUrl) {
      return false;
    }

    // Mystery Shopper Module (moduleId = 2)
    if (moduleId === 2) {
      const mysteryShopperPaths = [
        '/msm-checklist/',
        '/general-dashboard/',
        '/project-head-ui/',
        '/system-code/',
        '/user-management/',
      ];
      return mysteryShopperPaths.some(path => returnUrl.startsWith(path));
    }
    // Other modules
    else {
      const otherPaths = [
        '/project/',
        '/section/',
        '/tasks/',
        '/milestone/',
        '/project-cr/',
        '/tags/',
        '/user-role/',
        '/roles/',
        '/section-chart/',
        '/reports/',
        '/project-head/',
        '/dashboard',
        '/system-code/',
        '/user-management/',
      ];
      return otherPaths.some(path => returnUrl.startsWith(path));
    }
  }

  buildForm(): void {
    this.loginForm = this.fb.group({
      userName: [null, [Validators.required]],
      password: [null, [Validators.required]],
    });
  }
  setLanguageSubscriber(): void {
    this.language = this.translateService.currentLang;
    this.subscriptionTranslate = this.translateService.onLangChange.subscribe(
      val => {
        this.language = val.lang;
      },
      error => {},
      () => {}
    );
    this.subscriptions.push(this.subscriptionTranslate);
  }
  setPageTitle(): void {
    this.pageTitle.setTitleTranslated(`login.Title`);
  }

  getControl(controlName: string): any {
    return this.loginForm?.controls[controlName];
  }

  /**
   * Handle Microsoft sign-in button click
   */
  signInWithMicrosoft(): void {
    // Get Azure AD login configuration from API
    this.subscriptions.push(
      this.loginService.getAzureLoginData().subscribe(
        (loginData: any) => {
          if (loginData && loginData.tenantId && loginData.clientId) {
            // Get current return URL from query params or session
            const returnUrl =
              this.route.snapshot.queryParams['returnUrl'] ||
              this.currentUserService.getReturnUrl() ||
              '';

            // Build Microsoft OAuth authorization URL
            const scope = encodeURIComponent('openid profile email User.Read');
            const redirectUri = encodeURIComponent(
              window.location.origin + '/login-callback'
            );
            const responseMode = 'query'; // Using query instead of form_post for SPA

            const authUrl =
              `https://login.microsoftonline.com/${loginData.tenantId}/oauth2/v2.0/authorize?` +
              `client_id=${loginData.clientId}&` +
              `response_type=code&` +
              `response_mode=${responseMode}&` +
              `scope=${scope}&` +
              `redirect_uri=${redirectUri}&` +
              `state=${encodeURIComponent(returnUrl)}`;

            // Store return URL in session storage for callback
            if (returnUrl) {
              this.currentUserService.setReturnUrl(returnUrl);
            }

            // Redirect to Microsoft OAuth
            window.location.href = authUrl;
          } else {
            this.notificationService.showErrorTranslated(
              'users.login.microsoftInitFailed',
              ''
            );
          }
        },
        (error: any) => {
          this.notificationService.showErrorTranslated(
            'users.login.microsoftInitFailed',
            ''
          );
        }
      )
    );
  }

  /**
   * Navigate user to appropriate dashboard based on their role and module
   */
  private navigateBasedOnRole(user: UserLoggedIn): void {
    // Mystery Shopper Module (moduleId 2)
    if (user.moduleId === 2) {
      if (user.roleId === UmRole.ProjectHead) {
        this.router.navigate(['/msm-checklist/general-dashboard/divisions']);
      } else if (user.roleId === UmRole.Agent) {
        this.router.navigate(['/msm-checklist/agent-ui/dashboard']);
      } else if (user.roleId === UmRole.Stakeholder) {
        this.router.navigate(['/msm-checklist/general-dashboard/divisions']);
      } else if (user.roleId === UmRole.ProjectAdmin) {
        this.router.navigate(['/msm-checklist/project-admin-ui']);
      } else {
        // For other roles in Mystery Shopper module
        this.router.navigate(['/msm-checklist/action-plan/list']);
      }
    }
    // Project Tracker Module (moduleId 1) or other modules
    else {
      this.router.navigate(['/dashboard']);
    }
  }
}
